import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

export default function AtlasApprovals() {
  return (
    <>
      <NextSeo
        title="Review and Apply in Atlas | Chat.co"
        description="Approve each exact effect, dismiss proposals and handle expiry."
      />
      <DocsArticleLayout
        category="Atlas"
        categoryColor="text-blue-700"
        title="Review and Apply in Atlas"
        description="Approval belongs to the specific effect you can see and inspect."
        readingTime="5 min read"
        tableOfContents={[
          { id: 'exact-effect', title: 'Review the exact effect' },
          { id: 'decision', title: 'Approve, Apply or decline' },
          { id: 'expiry', title: 'Expiry and changed proposals' },
          { id: 'result', title: 'Confirm the result' },
        ]}
        prevArticle={{
          title: 'Getting started with Atlas',
          href: PageLink.docsAtlasGettingStarted,
        }}
        nextArticle={{
          title: 'Atlas knowledge and answer quality',
          href: PageLink.docsAtlasKnowledge,
        }}>
        <section id="exact-effect">
          <h2>Review the exact effect</h2>
          <DocsCallout title="A chat reply is not approval">
            Each consequential action needs its own review card and an explicit click on its
            approval button. An ordinary chat yes is not approval. Informational plans have no
            blanket Apply.
          </DocsCallout>
          <p>
            A plan explains proposed steps. It does not authorize all of them. If a step changes
            data, invokes paid testing, publishes content or sends something externally, inspect
            that effect separately when its review is offered.
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Confirm the account or bot being changed and the exact content or selected sources.
            </li>
            <li>For an external effect, check recipients, audience and destination.</li>
            <li>
              For spending, check the amount and currency when known, estimates and any stated
              unknown cost.
            </li>
            <li>
              Read whether the effect can be reversed and when approval expires. Do not assume Undo
              will be available.
            </li>
          </ul>
        </section>
        <section id="decision">
          <h2>Approve, Apply or decline</h2>
          <p>
            Use the approval button on the exact review currently displayed. Standard reviews show{' '}
            <strong>Approve</strong>, <strong>Approve &amp; send</strong>,{' '}
            <strong>Approve &amp; publish</strong> or <strong>Approve &amp; delete</strong>,
            depending on the effect. Hosted workflow reviews use <strong>Apply</strong>. These
            controls authorize only their own reviewed effect.
          </p>
          <p>
            To decline a proposal, select <strong>Decline</strong> on a standard review or{' '}
            <strong>Dismiss</strong> on a hosted workflow review. Decline or Dismiss does not undo
            an earlier completed effect. If an edit control is offered, use it to change the
            supported fields and then read the replacement review.
          </p>
          <p>
            For knowledge refresh, pages of unknown freshness are not refreshed unless you include
            them. Where Edit is offered, it can include only listed pages of unknown freshness. It
            cannot remove new or changed pages. If the fixed selection is wrong, do not Apply it:
            Dismiss stops that refresh, or request a revised proposal. A later training or test step
            still needs its own review.
          </p>
          <p>
            Review controls depend on capability availability for enrolled accounts with the
            required permissions. Seeing a proposal does not grant permission to execute it or
            enable a capability for your account.
          </p>
        </section>
        <section id="expiry">
          <h2>Expiry and changed proposals</h2>
          <p>
            Expired approvals cannot be applied. An expired card may offer{' '}
            <strong>Review again</strong> or <strong>Ask again</strong>. Both request a fresh
            review; neither reuses the old approval. An expired standard card may also offer
            Dismiss. Read the new card before deciding. A change to the draft, targets or selected
            pages also requires a fresh decision.
          </p>
          <p>
            If the app asks you to wait because Atlas is busy, allow the current request to settle.
            Never use a stale card to work around an unavailable or rejected review.
          </p>
        </section>
        <section id="result">
          <h2>Confirm the result</h2>
          <p>
            <strong>Sending…</strong> or <strong>Submitted — waiting for Atlas</strong> means
            confirmation is still pending. Approved on a standard card records your decision; it
            does not confirm the effect completed. Its after-click note says Logged in Activity.
            Check the newer task status and result evidence. For purchases, a prepared checkout link
            is not a completed payment; review checkout before completing a purchase there.
          </p>
          <DocsCallout variant="warning" title="Missing confirmation">
            Do not repeat a payment, send or data change when the outcome is unknown. Check the task
            and destination first, then follow{' '}
            <Link href={PageLink.docsAtlasTroubleshooting}>unknown outcome troubleshooting</Link>.
          </DocsCallout>
        </section>
      </DocsArticleLayout>
    </>
  );
}
